import React from "react";
import { Globe, Sparkles, MapPin } from "lucide-react";

/**
 * iter387 — MarketBadge: rendered above the pricing plan grid to give the user
 * an honest, transparent label about which regional pricing tier they are
 * currently being offered, with a manual override dropdown for EU
 * Regulation 2018/302 (no-geo-blocking) compliance.
 *
 * Markets:
 *   t1 — High-income premium (US, UK, CH, NO, AU, JP, ...) → gold "Premium region"
 *   eu — EU/EEA baseline anchor → neutral
 *   bg — BG + lower-PPP EU neighbours → blue "Regional pricing"
 *   t3 — Emerging markets (IN, BR, ID, PH, EG, ...) → teal "Designed for emerging markets"
 */

const MARKET_META = {
  t1: {
    bg: {
      title: "Премиум регион",
      sub: "Цените са анкори за пазари с висок доход. Може да превключиш ръчно.",
      icon: Sparkles,
      accent: "#d4a72c",
    },
    en: {
      title: "Premium region",
      sub: "Prices anchored to high-income markets. You may switch manually.",
      icon: Sparkles,
      accent: "#d4a72c",
    },
    es: {
  title: "Región premium",
  sub: "Precios anclados a mercados de altos ingresos. Puede cambiar manualmente.",
  icon: Sparkles,
  accent: "#d4a72c",
},
  },
  eu: {
    bg: {
      title: "Стандартни EU цени",
      sub: "Цените са в EUR. Може да превключиш ръчно.",
      icon: Globe,
      accent: "#0EA5E9",
    },
    en: {
      title: "Standard EU pricing",
      sub: "Prices in EUR. You may switch manually.",
      icon: Globe,
      accent: "#0EA5E9",
    },
  },
  bg: {
    bg: {
      title: "Регионални цени за вашия пазар",
      sub: "Адаптирани цени с PPP корекция. Може да превключиш ръчно.",
      icon: MapPin,
      accent: "#10B981",
    },
    en: {
      title: "Regional pricing for your market",
      sub: "PPP-adjusted regional prices. You may switch manually.",
      icon: MapPin,
      accent: "#10B981",
    },
  },
  t3: {
    bg: {
      title: "Достъпни цени за развиващи се пазари",
      sub: "Pricing-ът е оптимизиран за достъпност. Може да превключиш ръчно.",
      icon: Globe,
      accent: "#06B6D4",
    },
    en: {
      title: "Designed for emerging markets",
      sub: "Pricing optimised for affordability. You may switch manually.",
      icon: Globe,
      accent: "#06B6D4",
    },
  },
};

const OPTIONS_BG = [
  { value: "t1", label: "Премиум (US, UK, CH, AU…)" },
  { value: "eu", label: "Стандартни EU цени" },
  { value: "bg", label: "BG + Балкани (PPP)" },
  { value: "t3", label: "Развиващи се пазари" },
];
const OPTIONS_EN = [
  { value: "t1", label: "Premium (US, UK, CH, AU…)" },
  { value: "eu", label: "Standard EU pricing" },
  { value: "bg", label: "BG + Balkans (PPP)" },
  { value: "t3", label: "Emerging markets" },
];

export default function MarketBadge({ market, onMarketChange, language = "en", isDark = false }) {
  if (!market) return null;
  const meta = (MARKET_META[market] || MARKET_META.eu)[language] || (MARKET_META[market] || MARKET_META.eu).en;
  const Icon = meta.icon;
  const options = language === "bg" ? OPTIONS_BG : OPTIONS_EN;

  return (
    <div
      data-testid="pricing-market-badge"
      className="max-w-2xl mx-auto mb-8 rounded-xl border p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3"
      style={{
        background: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
        borderColor: isDark ? "rgba(148, 163, 184, 0.18)" : "#E5E7EB",
        boxShadow: isDark ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <div
        className="rounded-lg flex-shrink-0 w-10 h-10 flex items-center justify-center"
        style={{
          background: `${meta.accent}22`,
          color: meta.accent,
        }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-semibold"
          data-testid="pricing-market-badge-title"
          style={{ color: isDark ? "#f1f5f9" : "#1A1A2E" }}
        >
          {meta.title}
        </div>
        <div
          className="text-xs"
          data-testid="pricing-market-badge-sub"
          style={{ color: isDark ? "#94a3b8" : "#64748B" }}
        >
          {meta.sub}
        </div>
      </div>
      <select
        data-testid="pricing-market-selector"
        value={market}
        onChange={(e) => onMarketChange?.(e.target.value)}
        className="text-xs px-3 py-1.5 rounded-md border"
        style={{
          background: isDark ? "rgba(15, 23, 42, 0.8)" : "#F9FAFB",
          borderColor: isDark ? "rgba(148, 163, 184, 0.25)" : "#E5E7EB",
          color: isDark ? "#f1f5f9" : "#1A1A2E",
          cursor: "pointer",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
